// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — what the account's plan lets this browser do, checked where the
 * app does it (owner, 2026-10-03: "free plan but I can load everything").
 *
 * - **Streams:** a stream outside the plan's packages does not load; the
 *   plans open instead. The SOS catalogue is free. A load the room makes
 *   (following a presenter or a room's lead) is not checked: watching what
 *   someone shows is free, their plan covers it.
 * - **Data tools:** values under the pointer, pins and the graph, Analyze
 *   and its exports need a plan with `data` (Plus and every presenting plan).
 *
 * The stream files themselves are still public on the stream host, so this
 * stops the app, not a determined person reading the files directly; the
 * stream host's own check is separate work.
 */

import type { MessageKey } from '../i18n'
import type { Dataset } from '../types'
import { accountState, currentEntitlements, onAccountChange, type AccountState } from './account'
import { isEducationOnly, outsidePlan, packageOf, type StreamPackage } from './datasetTaxonomy'
import { MAX_PINS } from './measure/pins'
import { nextUtcMidnight, streamBytesToday } from './streamQuota'

/** Why a load was refused, for a caller that wants to say so (the AYNI chatbot). */
export type LoadRefusal = 'outside_plan'

/** The account, once the site has said who it is (it is 'unknown' while the page starts). */
export function accountKnown(): Promise<AccountState> {
  const now = accountState()
  if (now.status !== 'unknown') return Promise.resolve(now)
  return new Promise((resolve) => {
    let off: () => void = () => {}
    off = onAccountChange((state) => {
      if (state.status === 'unknown') return
      queueMicrotask(() => off())
      resolve(state)
    })
  })
}

/**
 * AYNI Studio: this page renders a paid order (render mode with a shot the
 * jobs API served under a job token the server verified). The order's plan
 * was checked when it was quoted, so the page plays its streams whatever
 * account the render worker's browser has (none).
 */
let renderJob = false
export function setRenderJobAuthorized(on: boolean): void {
  renderJob = on
}

/**
 * AYNI (Situation room, CR1): a paired wall display has no account (decision
 * CR-D2: no seat). Its licence's packages and globes, as `/api/situation/wall`
 * serves them (`'*'`: every package), are lent to it while it is paired.
 */
let wallGrant: { packages: readonly string[]; globes: number } | null = null

export function setWallGrant(grant: { packages: readonly string[]; globes: number } | null): void {
  wallGrant = grant ? { packages: [...grant.packages], globes: Number.isInteger(grant.globes) && grant.globes > 0 ? grant.globes : 1 } : null
}

/**
 * AYNI (owner, 2026-10-13: "the add-on packages from the teacher's
 * licence? yes"): a meeting lends its host's stream packages (`'*'`: every
 * package) to a participant while the sphere is in their hands — exploring
 * on their own, holding the presenter's globe, or driving one of its
 * globes. Set by main.ts from the room's status; null otherwise.
 */
let lentPackages: readonly string[] | null = null
const lentListeners = new Set<() => void>()

export function setLentPackages(packages: readonly string[] | null): void {
  const next = packages && packages.length ? [...packages] : null
  if (JSON.stringify(next) === JSON.stringify(lentPackages)) return
  lentPackages = next
  for (const listener of lentListeners) listener()
}

/** Called when what a meeting lends changes (Browse redraws its locks). Returns an unsubscribe. */
export function onLentPackagesChange(listener: () => void): () => void {
  lentListeners.add(listener)
  return () => lentListeners.delete(listener)
}

/** The packages this browser may stream now: its plan's, and what a meeting lends it. */
export function effectivePackages(): ReadonlySet<string> | 'all' {
  const own = currentEntitlements().packages
  if (own === 'all' || !lentPackages) return own
  if (lentPackages.includes('*')) return 'all'
  return new Set([...own, ...lentPackages])
}

/** The package a stream needs that the plan lacks, or null when it may load. Waits for the account. */
export async function streamLockedBy(dataset: Dataset): Promise<StreamPackage | null> {
  const pkg = packageOf(dataset)
  if (renderJob) return null
  // A stream only Education plays is checked even when the rule gives it no package (owner, 2026-10-18).
  if (!pkg && !isEducationOnly(dataset)) return null
  if (wallGrant && (wallGrant.packages.includes('*') || (pkg && wallGrant.packages.includes(pkg)))) return null
  await accountKnown()
  return outsidePlan(dataset, effectivePackages(), educationStreamsAllowed())
}

/**
 * AYNI (owner, 2026-11-03: "when in researcher I can still use GFW dataset
 * as Real Time overlays"): the real-time overlays of `ids` this browser may
 * start itself, in their order, and the streams it may not. A stream only
 * Education plays is refused on every other plan as Browse refuses it
 * (`streamLockedBy`); a package is still only a mark on an overlay. A
 * room's loads are not checked here: watching what someone shows is free.
 */
export async function rtOverlaysAllowed(
  ids: readonly string[],
  datasetOf: (id: string) => Dataset | undefined,
): Promise<{ allowed: string[]; refused: Dataset[] }> {
  const allowed: string[] = []
  const refused: Dataset[] = []
  for (const id of ids) {
    const dataset = datasetOf(id)
    if (dataset && isEducationOnly(dataset) && (await streamLockedBy(dataset)) !== null) refused.push(dataset)
    else allowed.push(id)
  }
  return { allowed, refused }
}

/** Whether the Real Time list offers this stream: not one only Education plays, on any other plan (`rtOverlaysAllowed`). */
export function rtOverlayOffered(dataset: Pick<Dataset, 'id'>): boolean {
  return !isEducationOnly(dataset) || educationStreamsAllowed()
}

/**
 * AYNI (owner, 2026-10-05): whether a meeting this browser is in lends it
 * the data tools — a class whose teacher's plan has `classroom`. Set by
 * main.ts from the room's status; it ends when the connection or the
 * meeting does. Lent tools are the Education level: the borrower's own
 * entitlements (free, for a student) keep whole-region statistics and the
 * Analyze exports locked.
 */
let lentData = false

export function setLentDataTools(on: boolean): void {
  lentData = on
}

/** Whether the data tools are on loan from a meeting right now. */
export function dataToolsLent(): boolean {
  return lentData
}

/** Whether the data tools are on: the plan includes them, or a meeting lends them (values, pins, graph, Analyze, exports). */
export function dataToolsAllowed(): boolean {
  return lentData || (accountState().status === 'signedIn' && currentEntitlements().data)
}

/**
 * Whether the plan has the data tools but not statistics over a whole
 * region (the dataset, a latitude band, a named region, the view): the
 * plans whose analysis is basic (decision R7b, 2026-10-05: whole-region
 * statistics are for Research and Education), Line and Box but not these;
 * `xr-cap-analysis-full` lifts it. A plan without the data tools is not
 * "locked" here: `dataToolsAllowed` already keeps it out of measuring.
 */
export function regionStatsLocked(): boolean {
  return dataToolsAllowed() && currentEntitlements().analysis !== 'full'
}

/**
 * Whether the plan has the data tools but only the pins' CSV, not the
 * Analyze exports (statistics, line, zonal profile): Education, unless
 * `xr-cap-exports-all`.
 */
export function analysisExportsLocked(): boolean {
  return dataToolsAllowed() && currentEntitlements().exports !== 'all'
}

/**
 * Most pins at once (Plus 2, owner 2026-10-06; the other plans every pin, `MAX_PINS`). Borrowed
 * tools are the Education level; without the data tools the Data tab says
 * why on its own, so the store keeps its usual size.
 */
export function maxPins(): number {
  if (lentData || !dataToolsAllowed()) return MAX_PINS
  return Math.min(MAX_PINS, currentEntitlements().pins)
}

/** Whether the plan has the data tools but not Line and Box (Plus: values and pins only). */
export function lineBoxLocked(): boolean {
  return dataToolsAllowed() && !lentData && !currentEntitlements().lineBox
}

/** Whether the plan has the data tools but no CSV at all, the pins' included (Plus). */
export function pinsExportLocked(): boolean {
  return dataToolsAllowed() && !lentData && currentEntitlements().exports === 'none'
}

/** Most real-time overlays at once the plan allows (Plus 1), or null for no plan limit. */
export function maxPlanOverlays(): number | null {
  return currentEntitlements().overlays
}

/** Today's stream volume against the plan's daily cap (megabytes; null cap: none), and when it starts again. */
export function streamQuotaToday(nowMs: number = Date.now()): { usedMB: number; capMB: number | null; resetsAt: number } {
  return { usedMB: streamBytesToday(nowMs) / 1_000_000, capMB: currentEntitlements().streamDailyMB, resetsAt: nextUtcMidnight(nowMs) }
}

/**
 * Whether opening this stream would go past the plan's daily volume (Plus 2
 * GB, owner 2026-10-17; Education 10 GB). Only streams in a package
 * count as locked; a stream already playing finishes (owner, 2026-10-06).
 */
export function streamQuotaReached(dataset: Dataset, nowMs: number = Date.now()): boolean {
  if (!packageOf(dataset)) return false
  const { usedMB, capMB } = streamQuotaToday(nowMs)
  return capMB !== null && usedMB >= capMB
}

/**
 * AYNI: whether the research tools are on (docs/AYNI_XR_RESEARCH_TIER_PLAN.md):
 * a frame held as a baseline, and later the globe difference and the line
 * over time. Research and Situation plans, or `xr-cap-research`. From the
 * account's own plan only: a class lends the data tools, never these.
 */
export function researchToolsAllowed(): boolean {
  if (accountState().status !== 'signedIn') return false
  const ent = currentEntitlements()
  return ent.data && ent.research
}

/**
 * AYNI (owner, 2026-10-17): anomalies against a held frame or a
 * climatology, and the publication figures and methods: the research
 * plans, and Media without the rest of the research tools (trend tests,
 * comparisons, threshold areas, Hovmöller, records, lab space).
 */
export function anomaliesAllowed(): boolean {
  if (accountState().status !== 'signedIn') return false
  const ent = currentEntitlements()
  return ent.data && (ent.research || ent.anomalies)
}

/**
 * AYNI (owner, 2026-10-17): Education does not see the research tools at
 * all, rather than locked with an upgrade hint, nor the contours or the
 * bar's Analyze button; a class borrowing a teacher's tools neither.
 */
export function teachingPlan(): boolean {
  return lentData || (accountState().status === 'signedIn' && currentEntitlements().teaching)
}

/** Whether a locked research tool is shown with its upgrade hint (not to Education, a class, or without the data tools). */
export function researchUpsellShown(): boolean {
  return dataToolsAllowed() && !teachingPlan()
}

/**
 * AYNI (owner, 2026-10-18): the streams only Education plays
 * (`EDUCATION_ONLY_STREAMS`), from the account's own plan: a class sees them
 * when its teacher shows them, as any load the room makes.
 */
export function educationStreamsAllowed(): boolean {
  return accountState().status === 'signedIn' && currentEntitlements().teaching
}

/** AYNI (owner, 2026-10-17): the View tab's colour palette, not on Free or Plus; a lent class keeps it hidden as its own plan says. */
export function paletteAllowed(): boolean {
  return accountState().status === 'signedIn' && currentEntitlements().palette
}

/**
 * AYNI (multi-globe plan, owner 2026-10-10): globes a meeting lends while
 * this browser is in it (an Education plan's meeting lends four, so a class
 * can hand four globes out). Set by main.ts from the room's grants.
 */
let lentGlobes = 0

export function setLentGlobes(n: number): void {
  lentGlobes = Number.isInteger(n) && n > 0 ? n : 0
}

/** Most globes side by side this plan allows (the device may allow fewer), or more while a meeting lends them. */
export function maxGlobes(): number {
  // A paid Studio render may be a split of up to four globes (multi-globe plan, phase 4); the order was checked when quoted.
  if (renderJob) return 4
  return Math.max(currentEntitlements().globes, lentGlobes, wallGrant?.globes ?? 0)
}

/** A package's name for people. */
export const PACKAGE_NAME: Record<StreamPackage, MessageKey> = {
  'xr-atmosphere': 'xr.package.atmosphere.name',
  'xr-ocean': 'xr.package.ocean.name',
  'xr-climate': 'xr.package.climate.name',
  'xr-fire-aq': 'xr.package.fireAq.name',
  'xr-hazards': 'browse.package.hazards',
}
